import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';
import { makeAutoObservable, runInAction } from 'mobx';
import RootStore from './RootStore';

// Enhanced theme system types
export type ThemeMode = 'light' | 'dark' | 'auto' | 'high-contrast';
export type ColorScheme = 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'custom';
export type FontSize = 'small' | 'medium' | 'large' | 'extra-large';
export type ThemeAnimation = 'none' | 'reduced' | 'standard' | 'enhanced';

export interface ThemeSettings {
  mode: ThemeMode;
  colorScheme: ColorScheme;
  fontSize: FontSize;
  animation: ThemeAnimation;
  customColors?: {
    primary: string;
    secondary: string;
    accent: string;
  };
}

export interface DesignTokens {
  spacing: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
  };
  borderRadius: {
    sm: number;
    md: number;
    lg: number;
  };
  shadows: {
    sm: string;
    md: string;
    lg: string;
  };
  transitions: {
    duration: {
      shortest: number;
      short: number;
      standard: number;
      long: number;
    };
    easing: {
      sharp: string;
      smooth: string;
      emphasized: string;
    };
  };
}

// Enhanced MUI module declarations
declare module '@mui/styles' {
  interface DefaultTheme extends Theme {}
}

declare module '@mui/material/styles' {
  interface Palette {
    gray: Palette['primary'];
    white: Palette['primary'];
    danger: Palette['primary'];
    neutral: Palette['primary'];
  }

  interface PaletteOptions {
    gray?: PaletteOptions['primary'];
    white?: PaletteOptions['primary'];
    danger?: PaletteOptions['primary'];
    neutral?: PaletteOptions['primary'];
  }

  interface Theme {
    designTokens: DesignTokens;
  }

  interface ThemeOptions {
    designTokens?: Partial<DesignTokens>;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    gray: true;
    white: true;
    danger: true;
    neutral: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    gray: true;
    white: true;
    danger: true;
    neutral: true;
  }
}

// Design tokens for consistent theming
const designTokens: DesignTokens = {
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
  },
  borderRadius: {
    sm: 4,
    md: 8,
    lg: 12,
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
  },
  transitions: {
    duration: {
      shortest: 150,
      short: 200,
      standard: 300,
      long: 500,
    },
    easing: {
      sharp: 'cubic-bezier(0.4, 0.0, 0.6, 1)',
      smooth: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
      emphasized: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
    },
  },
};

// Color scheme definitions
const colorSchemes: Record<ColorScheme, any> = {
  blue: {
    primary: { main: '#004080', light: '#336699', dark: '#00264d' },
    secondary: { main: '#008080', light: '#339999', dark: '#004d4d' },
  },
  green: {
    primary: { main: '#2e7d32', light: '#4caf50', dark: '#1b5e20' },
    secondary: { main: '#00695c', light: '#26a69a', dark: '#004d40' },
  },
  purple: {
    primary: { main: '#7b1fa2', light: '#ab47bc', dark: '#4a148c' },
    secondary: { main: '#512da8', light: '#7986cb', dark: '#303f9f' },
  },
  orange: {
    primary: { main: '#f57c00', light: '#ffb74d', dark: '#e65100' },
    secondary: { main: '#ff5722', light: '#ff8a65', dark: '#d84315' },
  },
  red: {
    primary: { main: '#d32f2f', light: '#ef5350', dark: '#b71c1c' },
    secondary: { main: '#c2185b', light: '#e91e63', dark: '#880e4f' },
  },
  custom: {
    primary: { main: '#004080', light: '#336699', dark: '#00264d' },
    secondary: { main: '#008080', light: '#339999', dark: '#004d4d' },
  },
};

// Font size scales
const fontSizeScales: Record<FontSize, any> = {
  small: {
    htmlFontSize: 14,
    fontSize: 12,
    h1: { fontSize: '2rem' },
    h2: { fontSize: '1.75rem' },
    h3: { fontSize: '1.5rem' },
    h4: { fontSize: '1.25rem' },
    h5: { fontSize: '1.125rem' },
    h6: { fontSize: '1rem' },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.75rem' },
    caption: { fontSize: '0.625rem' },
  },
  medium: {
    htmlFontSize: 16,
    fontSize: 14,
    h1: { fontSize: '2.5rem' },
    h2: { fontSize: '2rem' },
    h3: { fontSize: '1.75rem' },
    h4: { fontSize: '1.5rem' },
    h5: { fontSize: '1.25rem' },
    h6: { fontSize: '1.125rem' },
    body1: { fontSize: '1rem' },
    body2: { fontSize: '0.875rem' },
    caption: { fontSize: '0.75rem' },
  },
  large: {
    htmlFontSize: 18,
    fontSize: 16,
    h1: { fontSize: '3rem' },
    h2: { fontSize: '2.5rem' },
    h3: { fontSize: '2rem' },
    h4: { fontSize: '1.75rem' },
    h5: { fontSize: '1.5rem' },
    h6: { fontSize: '1.25rem' },
    body1: { fontSize: '1.125rem' },
    body2: { fontSize: '1rem' },
    caption: { fontSize: '0.875rem' },
  },
  'extra-large': {
    htmlFontSize: 20,
    fontSize: 18,
    h1: { fontSize: '3.5rem' },
    h2: { fontSize: '3rem' },
    h3: { fontSize: '2.5rem' },
    h4: { fontSize: '2rem' },
    h5: { fontSize: '1.75rem' },
    h6: { fontSize: '1.5rem' },
    body1: { fontSize: '1.25rem' },
    body2: { fontSize: '1.125rem' },
    caption: { fontSize: '1rem' },
  },
};

class ThemeStore {
  rootStore: RootStore;
  
  // Theme state
  currentTheme: Theme;
  themeSettings: ThemeSettings = {
    mode: 'auto',
    colorScheme: 'blue',
    fontSize: 'medium',
    animation: 'standard',
  };

  // Theme cache for performance
  private themeCache = new Map<string, Theme>();
  
  // System preferences
  private systemDarkMode = false;
  private reducedMotionPreference = false;
  private highContrastPreference = false;

  constructor(rootStore: RootStore, customTheme?: ThemeOptions) {
    makeAutoObservable(this, {
      rootStore: false,
    });
    
    this.rootStore = rootStore;
    this.detectSystemPreferences();
    this.setupSystemListeners();
    this.loadUserPreferences();
    
    if (customTheme) {
      this.currentTheme = createTheme(customTheme);
    } else {
      this.currentTheme = this.buildTheme();
    }
  }

  // ========================================
  // SYSTEM DETECTION AND LISTENERS
  // ========================================

  private detectSystemPreferences = () => {
    if (typeof window !== 'undefined') {
      try {
        this.systemDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        this.reducedMotionPreference = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.highContrastPreference = window.matchMedia('(prefers-contrast: high)').matches;
        
        // Apply system preferences
        if (this.themeSettings.mode === 'auto') {
          // Will be handled in buildTheme()
        }
        if (this.reducedMotionPreference) {
          this.themeSettings.animation = 'reduced';
        }
        if (this.highContrastPreference) {
          this.themeSettings.mode = 'high-contrast';
        }
      } catch (err) {
        console.warn('Could not detect system preferences:', err);
      }
    }
  };

  private setupSystemListeners = () => {
    if (typeof window !== 'undefined') {
      try {
        const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const highContrastQuery = window.matchMedia('(prefers-contrast: high)');

        const handleSystemChange = () => {
          runInAction(() => {
            this.systemDarkMode = darkModeQuery.matches;
            this.reducedMotionPreference = reducedMotionQuery.matches;
            this.highContrastPreference = highContrastQuery.matches;
            
            // Update theme if using auto mode
            if (this.themeSettings.mode === 'auto') {
              this.rebuildTheme();
            }
          });
        };

        darkModeQuery.addEventListener('change', handleSystemChange);
        reducedMotionQuery.addEventListener('change', handleSystemChange);
        highContrastQuery.addEventListener('change', handleSystemChange);
      } catch (err) {
        console.warn('Could not setup system preference listeners:', err);
      }
    }
  };

  // ========================================
  // THEME BUILDING
  // ========================================

  private buildTheme = (): Theme => {
    const cacheKey = this.getThemeCacheKey();
    const cachedTheme = this.themeCache.get(cacheKey);
    if (cachedTheme) {
      return cachedTheme;
    }

    const isDark = this.getEffectiveDarkMode();
    const isHighContrast = this.themeSettings.mode === 'high-contrast';
    const colorScheme = this.getEffectiveColorScheme();
    const fontScale = fontSizeScales[this.themeSettings.fontSize];
    
    const themeOptions: ThemeOptions = {
      designTokens,
      palette: {
        mode: isDark ? 'dark' : 'light',
        ...this.buildPalette(isDark, isHighContrast, colorScheme),
      },
      typography: {
        ...fontScale,
      },
      spacing: (factor: number) => designTokens.spacing.md * factor * 0.5, // 8px base
      shape: {
        borderRadius: designTokens.borderRadius.md,
      },
      transitions: {
        duration: this.getTransitionDurations(),
        easing: designTokens.transitions.easing,
      },
      components: this.buildComponentOverrides(isDark, isHighContrast),
    };

    const theme = createTheme(themeOptions);
    this.themeCache.set(cacheKey, theme);
    return theme;
  };

  private buildPalette = (isDark: boolean, isHighContrast: boolean, colorScheme: { primary?: { main: string }; secondary?: { main: string }; [key: string]: unknown }) => {
    const baseColors = {
      primary: {
        main: colorScheme.primary?.main || '#1976d2',
        contrastText: '#ffffff',
      },
      secondary: {
        main: colorScheme.secondary?.main || '#dc004e',
        contrastText: '#ffffff',
      },
      error: {
        main: '#f44336',
        light: '#e57373',
        dark: '#d32f2f',
        contrastText: '#ffffff',
      },
      warning: {
        main: '#ff9800',
        light: '#ffb74d',
        dark: '#f57c00',
        contrastText: '#ffffff',
      },
      info: {
        main: '#2196f3',
        light: '#64b5f6',
        dark: '#1976d2',
        contrastText: '#ffffff',
      },
      success: {
        main: '#4caf50',
        light: '#81c784',
        dark: '#388e3c',
        contrastText: '#ffffff',
      },
      gray: {
        main: isDark ? '#757575' : '#555555',
        light: isDark ? '#e0e0e0' : '#f5f5f5',
        dark: isDark ? '#424242' : '#333333',
        contrastText: isDark ? '#000000' : '#ffffff',
      },
      white: {
        main: '#ffffff',
        light: '#ffffff',
        dark: '#f5f5f5',
        contrastText: '#000000',
      },
      danger: {
        main: '#d32f2f',
        light: '#ef5350',
        dark: '#c62828',
        contrastText: '#ffffff',
      },
      neutral: {
        main: isDark ? '#424242' : '#9e9e9e',
        light: isDark ? '#757575' : '#e0e0e0',
        dark: isDark ? '#212121' : '#616161',
        contrastText: isDark ? '#ffffff' : '#ffffff',
      },
    };

    // Apply high contrast modifications
    if (isHighContrast) {
      Object.keys(baseColors).forEach(key => {
        const color = baseColors[key as keyof typeof baseColors];
        if (color && typeof color === 'object' && 'main' in color) {
          // Increase contrast for high contrast mode
          color.contrastText = isDark ? '#ffffff' : '#000000';
        }
      });
    }

    return baseColors;
  };

  private buildComponentOverrides = (isDark: boolean, isHighContrast: boolean) => {
    const animationDuration = this.getAnimationDuration();
    
    return {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            fontSize: fontSizeScales[this.themeSettings.fontSize].htmlFontSize,
          },
          body: {
            transition: animationDuration > 0 ? `background-color ${animationDuration}ms, color ${animationDuration}ms` : 'none',
          },
          '*': {
            scrollBehavior: this.themeSettings.animation === 'reduced' ? 'auto' : 'smooth',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1e1e1e' : '#555555',
            transition: animationDuration > 0 ? `background-color ${animationDuration}ms` : 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.md,
            transition: animationDuration > 0 ? `box-shadow ${animationDuration}ms` : 'none',
            border: isHighContrast ? '2px solid' : 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.sm,
            transition: animationDuration > 0 ? `all ${animationDuration}ms` : 'none',
            border: isHighContrast ? '2px solid transparent' : 'none',
            '&:focus-visible': {
              outline: isHighContrast ? '3px solid' : '2px solid',
              outlineOffset: '2px',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: designTokens.borderRadius.sm,
              transition: animationDuration > 0 ? `border-color ${animationDuration}ms` : 'none',
            },
          },
        },
      },
    };
  };

  // ========================================
  // HELPER METHODS
  // ========================================

  private getEffectiveDarkMode = (): boolean => {
    switch (this.themeSettings.mode) {
      case 'dark':
      case 'high-contrast':
        return true;
      case 'light':
        return false;
      case 'auto':
      default:
        return this.systemDarkMode;
    }
  };

  private getEffectiveColorScheme = () => {
    if (this.themeSettings.colorScheme === 'custom' && this.themeSettings.customColors) {
      return {
        primary: { main: this.themeSettings.customColors.primary },
        secondary: { main: this.themeSettings.customColors.secondary },
      };
    }
    return colorSchemes[this.themeSettings.colorScheme];
  };

  private getAnimationDuration = (): number => {
    if (this.themeSettings.animation === 'none' || this.reducedMotionPreference) {
      return 0;
    }
    switch (this.themeSettings.animation) {
      case 'reduced':
        return designTokens.transitions.duration.shortest;
      case 'enhanced':
        return designTokens.transitions.duration.long;
      case 'standard':
      default:
        return designTokens.transitions.duration.standard;
    }
  };

  private getTransitionDurations = () => {
    const basedurations = designTokens.transitions.duration;
    const multiplier = this.themeSettings.animation === 'reduced' || this.reducedMotionPreference ? 0 : 1;
    
    return {
      shortest: basedurations.shortest * multiplier,
      short: basedurations.short * multiplier,
      standard: basedurations.standard * multiplier,
      long: basedurations.long * multiplier,
    };
  };

  private getThemeCacheKey = (): string => {
    return JSON.stringify({
      mode: this.themeSettings.mode,
      colorScheme: this.themeSettings.colorScheme,
      fontSize: this.themeSettings.fontSize,
      animation: this.themeSettings.animation,
      customColors: this.themeSettings.customColors,
      systemDarkMode: this.systemDarkMode,
      reducedMotion: this.reducedMotionPreference,
      highContrast: this.highContrastPreference,
    });
  };

  private rebuildTheme = () => {
    this.currentTheme = this.buildTheme();
    this.applyThemeToDocument();
  };

  private applyThemeToDocument = () => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      const isDark = this.getEffectiveDarkMode();
      
      root.setAttribute('data-theme-mode', this.themeSettings.mode);
      root.setAttribute('data-color-scheme', this.themeSettings.colorScheme);
      root.setAttribute('data-font-size', this.themeSettings.fontSize);
      root.setAttribute('data-animation', this.themeSettings.animation);
      root.setAttribute('data-dark-mode', isDark.toString());
    }
  };

  private loadUserPreferences = () => {
    try {
      const saved = localStorage.getItem('theme-preferences');
      if (saved) {
        const preferences = JSON.parse(saved);
        this.themeSettings = { ...this.themeSettings, ...preferences };
      }
    } catch (err) {
      console.warn('Could not load theme preferences:', err);
    }
  };

  private saveUserPreferences = () => {
    try {
      localStorage.setItem('theme-preferences', JSON.stringify(this.themeSettings));
    } catch (err) {
      console.warn('Could not save theme preferences:', err);
    }
  };

  // ========================================
  // PUBLIC API
  // ========================================

  setThemeMode = (mode: ThemeMode) => {
    this.themeSettings.mode = mode;
    this.rebuildTheme();
    this.saveUserPreferences();
  };

  setColorScheme = (scheme: ColorScheme) => {
    this.themeSettings.colorScheme = scheme;
    this.rebuildTheme();
    this.saveUserPreferences();
  };

  setFontSize = (size: FontSize) => {
    this.themeSettings.fontSize = size;
    this.rebuildTheme();
    this.saveUserPreferences();
  };

  setAnimation = (animation: ThemeAnimation) => {
    this.themeSettings.animation = animation;
    this.rebuildTheme();
    this.saveUserPreferences();
  };

  setCustomColors = (colors: { primary: string; secondary: string; accent: string }) => {
    this.themeSettings.customColors = colors;
    if (this.themeSettings.colorScheme !== 'custom') {
      this.themeSettings.colorScheme = 'custom';
    }
    this.rebuildTheme();
    this.saveUserPreferences();
  };

  // Legacy methods for backwards compatibility
  setPrimaryColor = (color: string) => {
    this.setCustomColors({
      primary: color,
      secondary: this.themeSettings.customColors?.secondary || colorSchemes.blue.secondary.main,
      accent: this.themeSettings.customColors?.accent || colorSchemes.blue.primary.light,
    });
  };

  setSecondaryColor = (color: string) => {
    this.setCustomColors({
      primary: this.themeSettings.customColors?.primary || colorSchemes.blue.primary.main,
      secondary: color,
      accent: this.themeSettings.customColors?.accent || colorSchemes.blue.primary.light,
    });
  };

  getTheme = (): Theme => {
    return this.currentTheme;
  };

  // Enhanced getters
  get isDarkMode(): boolean {
    return this.getEffectiveDarkMode();
  }

  get isHighContrast(): boolean {
    return this.themeSettings.mode === 'high-contrast' || this.highContrastPreference;
  }

  get hasReducedMotion(): boolean {
    return this.themeSettings.animation === 'reduced' || this.reducedMotionPreference;
  }

  get currentColorScheme(): ColorScheme {
    return this.themeSettings.colorScheme;
  }

  get availableColorSchemes(): ColorScheme[] {
    return Object.keys(colorSchemes) as ColorScheme[];
  }

  get designTokens(): DesignTokens {
    return designTokens;
  }

  // Integration with UIStore
  syncWithUIStore = (uiStore: Record<string, unknown>) => {
    if (uiStore && uiStore.themeSettings) {
      this.themeSettings = uiStore.themeSettings as unknown as typeof this.themeSettings;
      this.rebuildTheme();
    }
  };

  // Cleanup
  dispose = () => {
    this.themeCache.clear();
    // Note: System listeners are not stored as references, so can't be removed
    // In a production app, you'd store references and remove them here
  };
}

export default ThemeStore;
